import { addDays } from './dates';
import { isAssigned, occursOn } from './recurrence';
import type { Completion, Task } from './types';

/**
 * Whether a member kept their routine on a day: they ticked at least half (and at least one) of the
 * routine tasks (water, walks, pills, workouts…) due for them. null when nothing was due.
 */
export function dayMet(tasks: Task[], ticked: Set<string>, uid: string, date: string): boolean | null {
  const due = tasks.filter((t) => occursOn(t, date) && isAssigned(t, uid));
  if (!due.length) return null;
  const n = due.filter((t) => ticked.has(`${date}__task-${t.id}__${uid}`)).length;
  return n >= 1 && n * 2 >= due.length;
}

/**
 * Consecutive kept days per member and for the duo ("both did it"), counted back from today.
 * Today only adds to a streak once it is met; an unfinished today never breaks one. Days with
 * nothing due are skipped.
 */
export function streaks(tasks: Task[], ticks: Completion[], uids: string[], today: string, maxDays = 60): { byUid: Record<string, number>; both: number } {
  const ticked = new Set(ticks.map((c) => `${c.date}__${c.itemKey}__${c.uid}`));
  const run = (met: (date: string) => boolean | null): number => {
    let n = 0;
    for (let i = 0; i < maxDays; i++) {
      const date = addDays(today, -i);
      const m = met(date);
      if (m === null) continue;
      if (!m) { if (i === 0) continue; break; }
      n++;
    }
    return n;
  };
  const byUid = Object.fromEntries(uids.map((u) => [u, run((d) => dayMet(tasks, ticked, u, d))]));
  const both = uids.length < 2 ? 0 : run((d) => {
    const all = uids.map((u) => dayMet(tasks, ticked, u, d));
    if (all.every((m) => m === null)) return null;
    return all.every((m) => m !== false);
  });
  return { byUid, both };
}
